/**
 * PRIVA Governance — SmartEWM SAP integration admin API client.
 *
 * Cookies carry the admin JWT (same pattern as every other admin page).
 * Secrets are NEVER sent back by the backend; we never log them here either.
 */
import axios from "axios";

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  withCredentials: true,
});

const BASE = "/api/admin/smartewm";

// Maps a signal type to its server-side diagnostic endpoint (configured mode).
const DIAG_PATH = {
  QUEUE: "warehouse-tasks",
  STOCK: "stock",
  WAVE: "waves",
};

export function extractError(e, fallback = "Request failed.") {
  const msg = e?.response?.data?.detail || e?.message || fallback;
  return typeof msg === "string" ? msg : fallback;
}

// ---- Signal Health ----
export const getSignalHealth = (clientId, clientSapId) =>
  apiClient
    .get(`${BASE}/signal-health`, { params: { clientId, clientSapId } })
    .then((r) => r.data);

// ---- SAP Connections ----
export const listConnections = (clientId) =>
  apiClient
    .get(`${BASE}/sap-connections`, { params: clientId ? { clientId } : {} })
    .then((r) => r.data);

export const createConnection = (payload) =>
  apiClient.post(`${BASE}/sap-connections`, payload).then((r) => r.data.connection);

export const updateConnection = (id, payload) =>
  apiClient.put(`${BASE}/sap-connections/${id}`, payload).then((r) => r.data.connection);

export const deleteConnection = (id) =>
  apiClient.delete(`${BASE}/sap-connections/${id}`).then((r) => r.data);

export const setDefaultConnection = (id) =>
  apiClient.post(`${BASE}/sap-connections/${id}/default`).then((r) => r.data.connection);

// ---- Signal Services ----
export const getSignalServices = (clientId, clientSapId) =>
  apiClient
    .get(`${BASE}/signal-services`, { params: { clientId, clientSapId } })
    .then((r) => r.data);

export const putSignalServices = (clientId, signals, clientSapId) =>
  apiClient
    .put(`${BASE}/signal-services`, { clientId, clientSapId, signals })
    .then((r) => r.data.config);

// ---- Signal Catalog / API Visibility (Phase 6 — governance transparency) ----
export const getSignalCatalog = () =>
  apiClient.get(`${BASE}/signal-catalog`).then((r) => r.data);

export const getSignalApi = (signalType, clientId, clientSapId) =>
  apiClient
    .get(`${BASE}/signal-services/${signalType}/api`, { params: { clientId, clientSapId } })
    .then((r) => r.data.api);

// ---- Inbound Domain configuration (Inbound Domain V1) ----
export const getInboundConfig = (clientId, clientSapId) =>
  apiClient
    .get(`${BASE}/inbound-config`, { params: { clientId, clientSapId } })
    .then((r) => r.data.config);

export const putInboundConfig = (clientId, config, clientSapId) =>
  apiClient
    .put(`${BASE}/inbound-config`, { clientId, clientSapId, config })
    .then((r) => r.data.config);

// ---- Outbound Domain configuration (Outbound Domain V1) ----
export const getOutboundConfig = (clientId, clientSapId) =>
  apiClient
    .get(`${BASE}/outbound-config`, { params: { clientId, clientSapId } })
    .then((r) => r.data.config);

export const putOutboundConfig = (clientId, config, clientSapId) =>
  apiClient
    .put(`${BASE}/outbound-config`, { clientId, clientSapId, config })
    .then((r) => r.data.config);

// ---- Tenant Config ----
export const getTenantConfig = (clientId) =>
  apiClient.get(`${BASE}/tenant-config`, { params: { clientId } }).then((r) => r.data.config);

export const putTenantConfig = (clientId, payload) =>
  apiClient.put(`${BASE}/tenant-config`, { clientId, ...payload }).then((r) => r.data.config);

// ---- Diagnostics (configured mode — no manual baseUrl) ----
export const runDiagnostic = (signalType, clientId) =>
  apiClient
    .post(`/api/test/sap/${DIAG_PATH[signalType]}`, { clientId })
    .then((r) => r.data);
